<template>
  <FilestackS3
    :s3path="s3BucketPath"
    :imageValidation="fillerImageValidation"
    :renameFileNameToUUID="true"
    :acceptedTypes="acceptedImageTypes"
    :maxSize="1000 * 1024 * 1024"
    @fileuploaded="files => handleFileUpload(files)"
    @init="$emit('init')"
    @close="$emit('close')"
  >
    <slot />
  </FilestackS3>
</template>

<script lang="ts">
import { SeriesResponse } from '@goldcast/api/backend';
import { defineComponent } from 'vue';
import { mapActions, mapGetters } from 'vuex';

import { FillerSlideAsset, uploadFillerSlide } from './fillerSlideLibraryService';

import FilestackS3 from '@/components/core/Filestack/FilestackS3.vue';
import { IMAGE_TYPES, GET_FILLER_SLIDE_LIBRARY_S3_PATH } from '@/constants';
import { processErrorParent } from '@/utils/utils';

export default defineComponent({
  name: 'UploadFillerSlide',
  components: { FilestackS3 },
  data() {
    return {
      acceptedImageTypes: IMAGE_TYPES,
      fillerImageValidation: {
        validationType: 'image',
        desiredImageRatio: '16:9',
        imageMaxWidth: 2400,
        imageMaxHeight: 2400,
        validationErrorMessage: 'The aspect ratio of your upload should match 1920x1080 (16:9) aspect ratio.'
      }
    };
  },
  computed: {
    ...mapGetters('org', { getCurrentOrg: 'getCurrentOrg' }),
    ...mapGetters('studioEventList', {
      event: 'getCurrentEvent'
    }),
    ...mapGetters('studioTemplateList', {
      template: 'getCurrentTemplate'
    }),
    ...mapGetters('studioSeriesList', {
      getCurrentSeriesById: 'getCurrentSeriesById'
    }),
    series(): SeriesResponse | null {
      if (!this.$route.params.seriesId) {
        return null;
      }

      return this.getCurrentSeriesById(this.$route.params.seriesId);
    },
    isRepeatingEvent(): boolean {
      return this.series?.type === 'repeating';
    },
    s3BucketPath(): string {
      return this.getCurrentOrg.id ? GET_FILLER_SLIDE_LIBRARY_S3_PATH(this.getCurrentOrg.id) : '';
    }
  },
  methods: {
    ...mapActions('studioToast', ['triggerToast']),
    handleFileUpload(files) {
      const fileName = files?.filesUploaded[0]?.originalFile?.name;
      const organizationId = this.getCurrentOrg.id;
      const nameArray = files?.filesUploaded[0]?.key.split('/');
      const broadcastFillerImageS3Path = nameArray.length
        ? `${this.s3BucketPath}${nameArray[nameArray.length - 1]}`
        : null;
      if (!fileName || !organizationId || !broadcastFillerImageS3Path) {
        this.handleError();
        return;
      }

      let workspaceId = '';
      if (this.$route.params.eventId || this.isRepeatingEvent) {
        workspaceId = this.event?.workspace?.id;
      } else if (this.$route.params.templateId) {
        workspaceId = this.template?.workspace?.id;
      }
      uploadFillerSlide({
        broadcastFillerImageS3Path,
        fileName,
        organizationId,
        workspace: workspaceId ?? null
      })
        .then((fillerSlideAsset: FillerSlideAsset) => {
          this.$emit('success', fillerSlideAsset);
        })
        .catch((error: Error) => {
          this.$emit('failure');
          this.handleError(processErrorParent(error.message));
        });
    },
    handleError(errorMessage?: string) {
      this.triggerToast({
        mode: 'error',
        msg: errorMessage || 'File upload failed'
      });
    }
  }
});
</script>
